import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { rolesAPI } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Plus, 
  Pencil, 
  Trash,
  ShieldCheck,
  Spinner,
  WarningCircle,
  Eye,
  PencilSimple,
  TrashSimple,
  LockKey,
  Users
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const MODULES = [
  { id: 'dashboard', label: 'Dashboard', description: 'Panel principal' },
  { id: 'simulacion', label: 'Simulación', description: 'Ejecutar simulaciones' },
  { id: 'escenarios', label: 'Escenarios', description: 'Gestión de escenarios clínicos' },
  { id: 'evaluaciones', label: 'Evaluaciones', description: 'OSCE y evaluaciones' },
  { id: 'reportes', label: 'Reportes', description: 'Informes y estadísticas' },
  { id: 'medicamentos', label: 'Medicamentos', description: 'Catálogo de medicamentos' },
  { id: 'acciones', label: 'Acciones Clínicas', description: 'Catálogo de acciones' },
  { id: 'diagnosticos', label: 'Diagnósticos', description: 'Catálogo de diagnósticos' },
  { id: 'tratamientos', label: 'Planes de Tratamiento', description: 'Catálogo de tratamientos' },
  { id: 'mantenedores', label: 'Mantenedores', description: 'Vías, presentaciones, etc.' },
  { id: 'usuarios', label: 'Usuarios', description: 'Gestión de usuarios' },
  { id: 'establecimientos', label: 'Establecimientos', description: 'Gestión de instituciones' },
  { id: 'roles', label: 'Roles', description: 'Gestión de roles y permisos' },
  { id: 'panel_instructor', label: 'Panel Instructor', description: 'Control de simulación en vivo' },
];

const ACTIONS = [
  { id: 'ver', label: 'Ver', icon: Eye },
  { id: 'crear', label: 'Crear', icon: Plus },
  { id: 'editar', label: 'Editar', icon: PencilSimple },
  { id: 'eliminar', label: 'Eliminar', icon: TrashSimple },
  { id: 'activar_desactivar', label: 'Activar/Desactivar', icon: LockKey },
  { id: 'asignar_niveles', label: 'Asignar Niveles', icon: Users },
];

const defaultRole = {
  nombre: '',
  descripcion: '',
  permisos: MODULES.map(m => ({ modulo: m.id, acciones: [] })),
  is_system: false
};

export default function RolesPage() {
  const { isSuperAdmin, hasPermission } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState(defaultRole);
  const [saving, setSaving] = useState(false);

  const canCreate = isSuperAdmin || hasPermission('roles', 'crear');
  const canEdit = isSuperAdmin || hasPermission('roles', 'editar');
  const canDelete = isSuperAdmin || hasPermission('roles', 'eliminar');

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const response = await rolesAPI.getAll();
      setRoles(response.data);
    } catch (error) {
      toast.error('Error al cargar roles');
      // Load default roles for display
      setRoles([
        { id: 'super_admin', nombre: 'Super Administrador', descripcion: 'Acceso total al sistema', is_system: true, permisos: [] },
        { id: 'admin', nombre: 'Administrador', descripcion: 'Administrador de establecimiento', is_system: true, permisos: [] },
        { id: 'instructor', nombre: 'Instructor', descripcion: 'Docente o instructor clínico', is_system: true, permisos: [] },
        { id: 'alumno', nombre: 'Alumno', descripcion: 'Estudiante de simulación', is_system: true, permisos: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        ...defaultRole,
        ...role,
        permisos: MODULES.map(m => {
          const existing = role.permisos?.find(p => p.modulo === m.id);
          return existing || { modulo: m.id, acciones: [] };
        })
      });
    } else {
      setEditingRole(null);
      setFormData(defaultRole);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRole(null);
    setFormData(defaultRole);
  };

  const handlePermissionChange = (moduleId, actionId, checked) => {
    setFormData(prev => ({
      ...prev,
      permisos: prev.permisos.map(p => {
        if (p.modulo === moduleId) {
          const acciones = checked
            ? [...p.acciones, actionId]
            : p.acciones.filter(a => a !== actionId);
          return { ...p, acciones };
        }
        return p;
      })
    }));
  };

  const handleSelectAllForModule = (moduleId, selectAll) => {
    setFormData(prev => ({
      ...prev,
      permisos: prev.permisos.map(p => {
        if (p.modulo === moduleId) {
          return { ...p, acciones: selectAll ? ACTIONS.map(a => a.id) : [] };
        }
        return p;
      })
    }));
  };

  const handleSave = async () => {
    if (!formData.nombre) {
      toast.error('Ingrese el nombre del rol');
      return;
    }

    setSaving(true);
    try {
      if (editingRole) {
        await rolesAPI.update(editingRole.id, formData);
        toast.success('Rol actualizado');
      } else {
        await rolesAPI.create(formData);
        toast.success('Rol creado');
      }
      handleCloseModal();
      loadRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    if (role.is_system) {
      toast.error('No se pueden eliminar roles del sistema');
      return;
    }
    if (!window.confirm(`¿Eliminar rol "${role.nombre}"?`)) return;

    try {
      await rolesAPI.delete(role.id);
      toast.success('Rol eliminado');
      loadRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getPermissionCount = (role) => {
    if (!role.permisos) return 0;
    return role.permisos.reduce((acc, p) => acc + (p.acciones?.length || 0), 0);
  };

  const getRoleBadgeColor = (roleName) => {
    const colors = {
      'super_admin': 'bg-red-100 text-red-700',
      'admin': 'bg-purple-100 text-purple-700',
      'instructor': 'bg-blue-100 text-blue-700',
      'alumno': 'bg-green-100 text-green-700',
    };
    return colors[roleName] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900" data-testid="roles-title">
              Roles y Permisos
            </h1>
            <p className="text-slate-500">
              Gestión de control de acceso basado en roles (RBAC)
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => handleOpenModal()} className="gap-2" data-testid="add-role-btn">
              <Plus size={18} />
              Nuevo Rol
            </Button>
          )}
        </div>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <LockKey size={24} className="text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Sistema de Permisos</p>
                <p className="text-sm text-blue-700">
                  Los permisos definen qué acciones puede realizar cada rol en cada módulo del sistema.
                  Los roles del sistema (marcados con 🔒) no pueden ser eliminados.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Roles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {roles.map(role => (
            <Card key={role.id} className="hover:shadow-md transition-shadow" data-testid={`role-card-${role.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getRoleBadgeColor(role.id)}`}>
                      <ShieldCheck size={24} />
                    </div>
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {role.nombre}
                        {role.is_system && <LockKey size={14} className="text-slate-400" />}
                      </CardTitle>
                      <CardDescription>{role.descripcion}</CardDescription>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenModal(role)} data-testid={`edit-role-${role.id}`}>
                        <Pencil size={16} />
                      </Button>
                      {canDelete && !role.is_system && (
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(role)} data-testid={`delete-role-${role.id}`}>
                          <Trash size={16} />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Users size={16} />
                    <span>{getPermissionCount(role)} permisos</span>
                  </div>
                  {role.is_system && (
                    <Badge variant="secondary">Sistema</Badge>
                  )}
                </div>
                {/* Permission preview */}
                <div className="mt-3 flex flex-wrap gap-1">
                  {role.permisos?.slice(0, 4).map(p => (
                    p.acciones?.length > 0 && (
                      <Badge key={p.modulo} variant="outline" className="text-xs">
                        {MODULES.find(m => m.id === p.modulo)?.label}
                      </Badge>
                    )
                  ))}
                  {role.permisos?.filter(p => p.acciones?.length > 0).length > 4 && (
                    <Badge variant="outline" className="text-xs">
                      +{role.permisos.filter(p => p.acciones?.length > 0).length - 4} más
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {roles.length === 0 && (
          <Card className="p-8 text-center">
            <WarningCircle size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No hay roles configurados</p>
          </Card>
        )}

        {/* Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{editingRole ? 'Editar Rol' : 'Nuevo Rol'}</DialogTitle>
              <DialogDescription>
                Configure los permisos para este rol en cada módulo del sistema
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nombre">Nombre del Rol *</Label>
                    <Input
                      id="nombre"
                      value={formData.nombre}
                      onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                      placeholder="Ej: Coordinador"
                      data-testid="role-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="descripcion">Descripción</Label>
                    <Input
                      id="descripcion"
                      value={formData.descripcion}
                      onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                      placeholder="Descripción del rol"
                    />
                  </div>
                </div>

                {/* Permissions Matrix */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 border-b">
                    <h3 className="font-medium">Matriz de Permisos</h3>
                  </div>
                  <div className="divide-y">
                    {MODULES.map(module => {
                      const modulePerms = formData.permisos.find(p => p.modulo === module.id);
                      const allSelected = modulePerms?.acciones?.length === ACTIONS.length;
                      
                      return (
                        <div key={module.id} className="px-4 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="font-medium text-sm">{module.label}</p>
                              <p className="text-xs text-slate-500">{module.description}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSelectAllForModule(module.id, !allSelected)}
                              className="text-xs"
                            >
                              {allSelected ? 'Quitar todos' : 'Seleccionar todos'}
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-3">
                            {ACTIONS.map(action => {
                              const isChecked = modulePerms?.acciones?.includes(action.id) || false;
                              const Icon = action.icon;
                              return (
                                <label
                                  key={action.id}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                                    isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                                  }`}
                                >
                                  <Checkbox
                                    checked={isChecked}
                                    onCheckedChange={(checked) => handlePermissionChange(module.id, action.id, checked)}
                                  />
                                  <Icon size={14} className={isChecked ? 'text-blue-600' : 'text-slate-400'} />
                                  <span className={`text-sm ${isChecked ? 'text-blue-700' : 'text-slate-600'}`}>
                                    {action.label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseModal}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-role-btn">
                {saving ? <Spinner size={18} className="animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
